"use client";

import * as React from "react";
import { cn } from "./cn";

export type PhoneInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "type"
> & {
  value: string;
  onChange: (value: string) => void;
  invalid?: boolean;
};

function formatDigitsToDisplay(digits: string): string {
  if (!digits) return "";
  const groups: string[] = [];
  const first = digits.slice(0, 4);
  groups.push(first);
  if (digits.length > 4) groups.push(digits.slice(4, 7));
  if (digits.length > 7) groups.push(digits.slice(7, 10));
  return groups.filter(Boolean).join(" ");
}

function normalizeInput(raw: string): string {
  let digits = raw.replace(/\D/g, "");
  if (digits.startsWith("84")) {
    digits = digits.slice(2);
  } else if (digits.startsWith("0")) {
    digits = digits.slice(1);
  }
  return digits.slice(0, 10);
}

function toE164(digits: string): string {
  if (!digits) return "";
  return `+84${digits}`;
}

function fromValue(value: string): string {
  if (!value) return "";
  if (value.startsWith("+84")) return value.slice(3);
  if (value.startsWith("84")) return value.slice(2);
  if (value.startsWith("0")) return value.slice(1);
  return value.replace(/\D/g, "");
}

export const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ value, onChange, className, invalid, disabled, ...props }, ref) => {
    const digits = fromValue(value);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const d = normalizeInput(e.target.value);
      onChange(toE164(d));
    };

    return (
      <div
        className={cn(
          "flex h-12 items-center gap-0 rounded-[var(--radius-sm)] border-[1.5px] bg-mist overflow-hidden transition-colors",
          "border-smoke focus-within:border-lime focus-within:bg-paper",
          invalid && "border-danger focus-within:border-danger",
          disabled && "opacity-60 pointer-events-none",
          className,
        )}
      >
        <div className="h-full flex items-center px-4 border-r border-smoke bg-white text-sm font-bold text-ink">
          🇻🇳 +84
        </div>
        <input
          ref={ref}
          type="tel"
          inputMode="numeric"
          placeholder="912 345 678"
          disabled={disabled}
          value={formatDigitsToDisplay(digits)}
          onChange={handleChange}
          className="h-full flex-1 bg-transparent px-4 text-[15px] font-satoshi font-medium text-ink placeholder:text-ash outline-none"
          {...props}
        />
      </div>
    );
  },
);
PhoneInput.displayName = "PhoneInput";
